import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';
import { AvailabilityReportItem } from '@/types';
import { clsx } from 'clsx';
import {
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import type { SortingStrategy } from '@dnd-kit/sortable';
import { ColumnHeader } from '../helpers/ColumnHeader';
import { AvailabilityTableSkeleton, storeAvailabilityColumnWidths } from './AvailabilityTableSkeleton';
import type { ColumnPinSide } from '@/stores/availability/availability-column-store';
import { highlightText } from '@/utils/inventory/search-highlight';
import { formatBenchmarkFee } from '@/utils/availability';
import { borderShadow, SELECTION_FILL, ROW_HIGHLIGHT_SHADOW, HEADER_COL_SELECTED_SHADOW } from '@/utils/cell-selection-styles';

import { useAvailabilityCrossDnd } from '@/features/availability/components/AvailabilityCrossDndContext';
import { MagnifyingGlassIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { GripDotsIcon, smoothScroll as sharedSmoothScroll, getColWidth as sharedGetColWidth } from '@/components/shared/table-utils';
import { VirtualizedGrid } from '@/components/shared/VirtualizedGrid';
import {
  GRID_HEADER_HEIGHT_STYLE,
  GRID_ROW_HEIGHT_PX,
  GRID_ROW_HEIGHT_STYLE,
} from '@/components/shared/grid-style';

const noDisplacementStrategy: SortingStrategy = () => null;

type AvailabilityColumnDef = ColumnDef<AvailabilityReportItem, unknown>;

const COL_GAP_PX = 12;

type SortableHeaderWrapperProps = {
  id: string;
  children: (handle: React.ReactNode) => React.ReactNode;
  enabled: boolean;
  showHandle?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onContextMenu?: (e: React.MouseEvent) => void;
};

const SortableHeaderWrapper: React.FC<SortableHeaderWrapperProps> = ({
  id,
  children,
  enabled,
  showHandle = true,
  className,
  style,
  onContextMenu,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id, disabled: !enabled });
  const { activeDragSource, tableOverColumnId } = useAvailabilityCrossDnd();
  const showDropIndicator = tableOverColumnId === id && activeDragSource != null;

  const dragStyle: React.CSSProperties = {
    ...style,
    visibility: (isDragging && activeDragSource === 'table') ? 'hidden' as const : undefined,
  };

  const handle = enabled && showHandle ? (
    <span
      className="flex flex-shrink-0 items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 self-center"
      {...attributes}
      {...listeners}
      tabIndex={0}
      role="button"
      aria-label="Drag to reorder column"
    >
      <GripDotsIcon className="h-4 w-4" />
    </span>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      className={className}
      style={dragStyle}
      onContextMenu={onContextMenu}
      data-column-id={id}
    >
      {showDropIndicator && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
      )}
      {children(handle)}
    </div>
  );
};

type AvailabilityTableProps = {
  data: AvailabilityReportItem[];
  columns: AvailabilityColumnDef[];
  sorting: { id: string; desc: boolean }[];
  onSortChange: (sorting: { id: string; desc: boolean }[]) => void;
  searchQuery: string;
  columnWidths: Record<string, number>;
  isLoading: boolean;
  error: Error | null;
  hoveredColumn: string | null;
  onHoveredColumnChange: (column: string | null) => void;
  onShowScrollTop: (show: boolean) => void;
  onSwitchToInventory: () => void;
  onSearchQueryChange: (query: string) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  accountForClientTransfers: boolean;
  onTradeEntry?: (item: AvailabilityReportItem) => void;
  pinnedLeft?: string[];
  pinnedRight?: string[];
  onPinColumn?: (columnId: string, side: ColumnPinSide) => void;
  activeFilterColumnIds?: string[];
  onFilterColumnClick?: (columnId: string) => void;
  filterableColumnIds?: Set<string>;
  onContextMenuColumn?: (e: React.MouseEvent, columnId: string) => void;
  onContextMenuCell?: (e: React.MouseEvent, columnId: string, item: AvailabilityReportItem) => void;
  isCellSelected?: (row: number, col: number) => boolean;
  isCellActive?: (row: number, col: number) => boolean;
  isRowSelected?: (row: number) => boolean;
  isColSelected?: (col: number) => boolean;
  getCellBorders?: (row: number, col: number) => import('@/hooks/useCellSelection').CellBorders | null;
  colIdToIndex?: Map<string, number>;
  /** DO NOT REMOVE — intentionally unused inside component body.
   *  Changing this value forces React.memo to re-render so that
   *  stable selection query callbacks (isCellSelected, etc.) pick up fresh ref data. */
  selectionTrigger?: unknown;
  ensureCellSelectedForContextMenu?: (columnId: string, rowIndex: number) => void;
  onCellMouseDown?: (e: React.MouseEvent, columnId: string, rowIndex: number) => void;
  onCellMouseEnter?: (e: React.MouseEvent, columnId: string, rowIndex: number) => void;
  onCellMouseUp?: () => void;
  isColumnsTabActive?: boolean;
  highlightedColumnIds?: string[];
};

const PIN_ICON_EXTRA_PX = 22;
const FILTER_ICON_EXTRA_PX = 24;

const getColWidth = (
  col: AvailabilityColumnDef,
  columnWidths: Record<string, number>,
  pinnedSet?: Set<string>,
  filterableSet?: Set<string>,
): number => {
  const cid = getColumnId(col);
  return sharedGetColWidth(cid, columnWidths[cid], col.minSize, pinnedSet, filterableSet, PIN_ICON_EXTRA_PX, FILTER_ICON_EXTRA_PX);
};

export const AvailabilityTable: React.FC<AvailabilityTableProps> = React.memo(({
  data,
  columns,
  sorting,
  onSortChange,
  searchQuery,
  columnWidths,
  isLoading,
  error,
  hoveredColumn,
  onHoveredColumnChange,
  onShowScrollTop,
  onSwitchToInventory,
  onSearchQueryChange,
  scrollContainerRef,
  accountForClientTransfers,
  onTradeEntry,
  pinnedLeft = [],
  pinnedRight = [],
  onPinColumn,
  activeFilterColumnIds,
  onFilterColumnClick,
  filterableColumnIds,
  onContextMenuColumn,
  onContextMenuCell,
  isCellSelected,
  isCellActive,
  isRowSelected,
  isColSelected,
  getCellBorders,
  colIdToIndex,
  ensureCellSelectedForContextMenu,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  isColumnsTabActive = false,
  highlightedColumnIds,
}) => {
  const internalParentRef = useRef<HTMLDivElement>(null);
  const parentRef = scrollContainerRef || internalParentRef;

  const toggleSort = (columnId: string) => {
    if (columnId === 'rowNumber') return;
    const existing = sorting.find((s) => s.id === columnId);
    if (!existing) {
      onSortChange([{ id: columnId, desc: false }]);
    } else if (!existing.desc) {
      onSortChange([{ id: columnId, desc: true }]);
    } else {
      onSortChange([]);
    }
  };

  // LR-600: isLoading is a REQUIRED dep — while loading this component returns
  // the skeleton, so the scroll container does not exist and the first run bails;
  // without re-running when the grid appears the listener was never attached and
  // the scroll-to-top button never showed on a cold load.
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const scrollTop = scrollElement.scrollTop;
      onShowScrollTop(scrollTop > 500);
    };

    handleScroll();
    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [onShowScrollTop, parentRef, isLoading]);

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);
  const allPinnedSet = useMemo(() => new Set([...pinnedLeft, ...pinnedRight]), [pinnedLeft, pinnedRight]);

  // Persist rendered column widths (including icon extras) so the skeleton matches.
  // Safe from feedback loops because calculateAvailabilityColumnWidths no longer
  // reads stored widths as a floor — it calculates purely from data.
  useEffect(() => {
    if (!isLoading && data.length > 0) {
      const rendered: Record<string, number> = {};
      for (const col of columns) {
        const cid = getColumnId(col);
        rendered[cid] = getColWidth(col, columnWidths, allPinnedSet, filterableColumnIds);
      }
      storeAvailabilityColumnWidths(rendered);
    }
  }, [isLoading, data.length, columnWidths, columns, allPinnedSet, filterableColumnIds]);

  // Ctrl+Arrow: scroll to edges (reads ref inside handler to avoid stale capture)
  useEffect(() => {
    const animIdRef = { current: 0 };
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const el = parentRef.current;
      if (!el) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          sharedSmoothScroll(el, 0, el.scrollLeft, animIdRef);
          break;
        case 'ArrowDown':
          e.preventDefault();
          sharedSmoothScroll(el, el.scrollHeight, el.scrollLeft, animIdRef);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          sharedSmoothScroll(el, el.scrollTop, 0, animIdRef);
          break;
        case 'ArrowRight':
          e.preventDefault();
          sharedSmoothScroll(el, el.scrollTop, el.scrollWidth, animIdRef);
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      cancelAnimationFrame(animIdRef.current);
    };
  }, [parentRef]);

  const { leftCols, centerCols, rightCols, leftWidth, rightWidth } = useMemo(() => {
    const left: AvailabilityColumnDef[] = [];
    const center: AvailabilityColumnDef[] = [];
    const right: AvailabilityColumnDef[] = [];
    columns.forEach((col) => {
      const cid = getColumnId(col);
      if (pinnedLeftSet.has(cid)) left.push(col);
      else if (pinnedRightSet.has(cid)) right.push(col);
      else center.push(col);
    });
    let lw = 0;
    for (const col of left) lw += getColWidth(col, columnWidths, allPinnedSet, filterableColumnIds) + COL_GAP_PX;
    let rw = 0;
    for (const col of right) rw += getColWidth(col, columnWidths, allPinnedSet, filterableColumnIds) + COL_GAP_PX;
    return { leftCols: left, centerCols: center, rightCols: right, leftWidth: lw + (left.length > 0 ? 10 : 0), rightWidth: rw + (right.length > 0 ? 16 : 0) };
  }, [columns, columnWidths, pinnedLeftSet, pinnedRightSet, allPinnedSet, filterableColumnIds]);

  const hasPinnedLeft = leftCols.length > 0;
  const hasPinnedRight = rightCols.length > 0;

  const headerColumnIds = useMemo(
    () => columns.map((c) => getColumnId(c)),
    [columns]
  );

  const renderCell = useCallback((columnId: string, item: AvailabilityReportItem, rowIndex: number) => {
    if (columnId === 'rowNumber') {
      return <span className="text-xs text-gray-500 w-full text-center block">{rowIndex + 1}</span>;
    }
    if (columnId === 'symbol_id') {
      const text = searchQuery.trim() && item.symbol_id.toLowerCase().includes(searchQuery.trim().toLowerCase())
        ? highlightText(item.symbol_id, searchQuery) : item.symbol_id;
      return (
        <span className="group/symbol inline-flex items-center gap-1 min-w-0">
          <span className="text-sm text-gray-700 truncate">{text}</span>
          <button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSwitchToInventory(); onSearchQueryChange(item.symbol_id); }}
            className="opacity-0 group-hover/symbol:opacity-100 transition-opacity flex-shrink-0 p-0.5 hover:bg-gray-200 rounded"
            title="View in Inventory" type="button"
          >
            <MagnifyingGlassIcon className="h-3 w-3 text-blue-600" />
          </button>
        </span>
      );
    }
    if (columnId === 'sedol') {
      const text = searchQuery.trim() && item.sedol.toLowerCase().includes(searchQuery.trim().toLowerCase())
        ? highlightText(item.sedol, searchQuery) : item.sedol;
      return (
        <span className="group/sedol inline-flex items-center gap-1 min-w-0">
          <span className="text-xs text-gray-500 truncate">{text}</span>
          <button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSwitchToInventory(); onSearchQueryChange(item.sedol); }}
            className="opacity-0 group-hover/sedol:opacity-100 transition-opacity flex-shrink-0 p-0.5 hover:bg-gray-200 rounded"
            title="Show in Inventory" type="button"
          >
            <MagnifyingGlassIcon className="h-3 w-3 text-blue-600" />
          </button>
        </span>
      );
    }
    if (columnId === 'isin') {
      const text = searchQuery.trim() && item.isin.toLowerCase().includes(searchQuery.trim().toLowerCase())
        ? highlightText(item.isin, searchQuery) : item.isin;
      return (
        <span className="group/isin inline-flex items-center gap-1 min-w-0">
          <span className="text-xs text-gray-500 truncate">{text}</span>
          <button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSwitchToInventory(); onSearchQueryChange(item.isin); }}
            className="opacity-0 group-hover/isin:opacity-100 transition-opacity flex-shrink-0 p-0.5 hover:bg-gray-200 rounded"
            title="Show in Inventory" type="button"
          >
            <MagnifyingGlassIcon className="h-3 w-3 text-blue-600" />
          </button>
        </span>
      );
    }
    if (columnId === 'quantity') {
      if (accountForClientTransfers && item.pending_transfers > 0) {
        return (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{item.adjusted_quantity.toLocaleString('en-US')}</span>
            <span className="text-gray-400 line-through text-xs">{item.quantity.toLocaleString('en-US')}</span>
          </div>
        );
      }
      return <span className="font-medium text-gray-900">{item.quantity.toLocaleString('en-US')}</span>;
    }
    if (columnId === 'benchmark_fee') {
      // Two decimals like the Inventory tab — Math.round here made the two
      // tabs (and the CSV export) disagree on the same security (LR-599).
      return <span className="font-medium text-gray-900">{formatBenchmarkFee(item.benchmark_fee)}</span>;
    }
    if (columnId === 'ca') {
      return <span className="text-sm text-gray-500">{item.ca}</span>;
    }
    if (columnId === 'transfers') {
      if (item.pending_transfers > 0) {
        return (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
            Out: {item.pending_transfers.toLocaleString('en-US')}
          </span>
        );
      }
      return <span className="text-sm text-gray-500">-</span>;
    }
    if (columnId === 'counterparties') {
      return (
        <div className="flex items-center">
          {item.counterparties.map((cp, cpIndex) => {
            const percentage = item.quantity > 0
              ? ((cp.quantity / item.quantity) * 100).toFixed(1)
              : '0.0';
            return (
              <span key={`${cp.name}-${cpIndex}`} className="group/cp inline-flex items-center gap-1">
                <span className="font-medium text-blue-600">{cp.name}:</span>
                <span className="font-semibold">{cp.quantity.toLocaleString('en-US')}</span>
                <span className="text-gray-500">({percentage}%)</span>
                <span className="inline-flex items-center" style={{ width: '16px', marginLeft: '4px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); e.preventDefault();
                      onSwitchToInventory();
                      onSearchQueryChange(`${item.symbol_id} + ${cp.name}`);
                    }}
                    className="opacity-0 group-hover/cp:opacity-100 transition-opacity p-0 hover:bg-gray-200 rounded"
                    title="View in Inventory" type="button"
                  >
                    <MagnifyingGlassIcon className="h-3 w-3 text-blue-600" />
                  </button>
                </span>
                {cpIndex < item.counterparties.length - 1 && (
                  <span className="text-gray-400" style={{ marginRight: '16px', marginLeft: '4px' }}> | </span>
                )}
              </span>
            );
          })}
        </div>
      );
    }
    return null;
  }, [searchQuery, accountForClientTransfers, onSwitchToInventory, onSearchQueryChange]);

  const { activeDragSource, sidebarOverColumnId } = useAvailabilityCrossDnd();
  const isSidebarDragOverTable =
    activeDragSource === 'sidebar' &&
    sidebarOverColumnId != null &&
    !sidebarOverColumnId.startsWith('sidebar:');
  const tableOverColumnId = sidebarOverColumnId && !sidebarOverColumnId.startsWith('sidebar:') ? sidebarOverColumnId : null;

  const NUMERIC_COLUMNS = new Set(['quantity', 'benchmark_fee', 'transfers']);

  const renderHeaderGroup = (cols: AvailabilityColumnDef[]) =>
    cols.map((column) => {
      const columnId = getColumnId(column);
      const sortState = sorting.find((s) => s.id === columnId);
      const width = getColWidth(column, columnWidths, allPinnedSet, filterableColumnIds);
      const headerText = typeof column.header === 'string' ? column.header : 'Column';
      const align = NUMERIC_COLUMNS.has(columnId) ? 'right' : 'left';
      const pinSide: ColumnPinSide = columnId === 'rowNumber' ? null : pinnedLeftSet.has(columnId) ? 'left' : pinnedRightSet.has(columnId) ? 'right' : null;
      const isDraggable = columnId !== 'rowNumber';
      const isHighlighted = highlightedColumnIds?.includes(columnId) ?? false;
      const headerColIdx = colIdToIndex?.get(columnId);
      const isHeaderColSelected = headerColIdx !== undefined && isColSelected?.(headerColIdx);

      return (
        <SortableHeaderWrapper
          key={columnId}
          id={columnId}
          enabled={isDraggable}
          showHandle={isColumnsTabActive}
          className={clsx('relative flex flex-shrink-0 items-center', columnId === 'rowNumber' ? '' : 'px-1.5 box-content', isHighlighted && 'self-stretch -my-2 py-2 bg-blue-50 animate-column-highlight-out', isHeaderColSelected && 'self-stretch -mb-2 pb-2')}
          style={{
            width: `${width}px`,
            minWidth: `${width}px`,
            ...(isHeaderColSelected ? { boxShadow: HEADER_COL_SELECTED_SHADOW } : undefined),
          }}
          onContextMenu={onContextMenuColumn ? (e) => onContextMenuColumn(e, columnId) : undefined}
        >
          {(handle) => (
            <>
              {isSidebarDragOverTable && tableOverColumnId === columnId && (
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10" />
              )}
              <ColumnHeader
                columnId={columnId}
                headerText={headerText}
                width={width}
                sortState={sortState}
                onSort={toggleSort}
                isLoading={isLoading}
                hasError={!!error}
                hoveredColumn={hoveredColumn}
                onHover={onHoveredColumnChange}
                align={align}
                pinSide={pinSide}
                onUnpin={onPinColumn ? () => onPinColumn(columnId, null) : undefined}
                filterable={filterableColumnIds?.has(columnId)}
                onFilterClick={onFilterColumnClick}
                hasActiveFilter={activeFilterColumnIds?.includes(columnId)}
                hideControls={isColumnsTabActive}
                dragHandle={handle}
              />
            </>
          )}
        </SortableHeaderWrapper>
      );
    });

  const renderBodyGroup = (cols: AvailabilityColumnDef[], item: AvailabilityReportItem, rowIndex: number) =>
    cols.map((column) => {
      const columnId = getColumnId(column);
      const width = getColWidth(column, columnWidths, allPinnedSet, filterableColumnIds);
      const ci = colIdToIndex?.get(columnId);
      const selected = ci !== undefined && isCellSelected?.(rowIndex, ci);
      const active = ci !== undefined && isCellActive?.(rowIndex, ci);
      const showSelectionFill = selected && !active;
      const isNumeric = NUMERIC_COLUMNS.has(columnId);
      const isCellHighlighted = highlightedColumnIds?.includes(columnId) ?? false;
      const isRowNumberCol = columnId === 'rowNumber';
      const isCellInPinZone = pinnedLeftSet.has(columnId) || pinnedRightSet.has(columnId) || isRowNumberCol;
      const rowHighlighted = isRowNumberCol && isRowSelected?.(rowIndex);
      const borders = selected && ci !== undefined ? getCellBorders?.(rowIndex, ci) : null;
      const selectionShadow = borders ? borderShadow(borders) : undefined;

      return (
        <div
          key={columnId}
          className={clsx(
            'self-stretch flex items-center min-w-0',
            !isCellInPinZone && '-my-2 py-2',
            columnId === 'rowNumber' ? 'text-sm' : 'px-1.5 box-content text-sm',
            isNumeric && 'justify-end',
            columnId !== 'rowNumber' && columnId !== 'counterparties' && columnId !== 'symbol_id' && columnId !== 'sedol' && columnId !== 'isin' && 'overflow-hidden text-ellipsis whitespace-nowrap',
            isCellHighlighted && 'bg-blue-50 animate-column-highlight-out',
          )}
          style={{
            width: `${width}px`,
            minWidth: `${width}px`,
            ...(selected && selectionShadow
              ? {
                  boxShadow: selectionShadow,
                  zIndex: 1,
                  ...(showSelectionFill ? { background: SELECTION_FILL } : undefined),
                }
              : undefined),
            ...(rowHighlighted && !selected ? { boxShadow: ROW_HIGHLIGHT_SHADOW } : undefined),
            cursor: isRowNumberCol ? 'pointer' : 'default',
          }}
          onContextMenu={onContextMenuCell ? (e) => {
            if (e.ctrlKey || e.metaKey) return;
            e.preventDefault();
            e.stopPropagation();
            ensureCellSelectedForContextMenu?.(columnId, rowIndex);
            onContextMenuCell(e, columnId, item);
          } : undefined}
          onMouseDown={onCellMouseDown ? (e) => onCellMouseDown(e, columnId, rowIndex) : undefined}
          onMouseEnter={onCellMouseEnter ? (e) => onCellMouseEnter(e, columnId, rowIndex) : undefined}
          onMouseUp={onCellMouseUp}
        >
          {renderCell(columnId, item, rowIndex)}
        </div>
      );
    });

  if (isLoading) {
    return <AvailabilityTableSkeleton columns={columns} />;
  }

  const headerNode = (
    <SortableContext items={headerColumnIds} strategy={noDisplacementStrategy}>
      {hasPinnedLeft && (
        <div
          className="sticky left-0 z-[12] flex items-center self-stretch bg-gray-50 pl-3 pr-3 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px` }}
        >
          {renderHeaderGroup(leftCols)}
        </div>
      )}
      <div className={clsx('flex items-center self-stretch py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {renderHeaderGroup(centerCols)}
      </div>
      {hasPinnedRight && (
        <div
          className="sticky right-0 z-[12] flex items-center self-stretch bg-gray-50 pl-3 pr-7 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px` }}
        >
          {renderHeaderGroup(rightCols)}
        </div>
      )}
    </SortableContext>
  );

  let bodyOverride: React.ReactNode = null;
  if (error) {
    bodyOverride = (
      <div className="flex items-center ml-[37vw] py-12">
        <div className="text-center">
          <div className="mb-2 text-red-600">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">Failed to load data</p>
        </div>
      </div>
    );
  } else if (data.length === 0) {
    bodyOverride = (
      <div className="flex items-center ml-[37vw] py-12">
        <div className="text-center">
          <p className="text-sm text-gray-500">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <VirtualizedGrid<AvailabilityReportItem>
      rows={data}
      estimateRowHeight={GRID_ROW_HEIGHT_PX}
      overscan={5}
      scrollContainerRef={parentRef}
      scrollContainerProps={{ 'data-availability-table-scroll-container': true } as React.HTMLAttributes<HTMLDivElement>}
      header={headerNode}
      headerClassName="shrink-0"
      headerStyle={GRID_HEADER_HEIGHT_STYLE}
      bodyOverride={bodyOverride}
      renderRow={({ row: item, index, virtualRow, style }) => (
        <div key={virtualRow?.key ?? index} style={style}>
          <div className="relative flex items-center border-b border-gray-200 hover:bg-gray-50 group" style={GRID_ROW_HEIGHT_STYLE}>
            {hasPinnedLeft && (
              <div
                className="sticky left-0 z-[2] flex items-center self-stretch bg-white pl-3 pr-3 py-2 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
                style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px` }}
              >
                {renderBodyGroup(leftCols, item, index)}
              </div>
            )}
            <div className={clsx('flex items-center py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
              {renderBodyGroup(centerCols, item, index)}
            </div>
            {hasPinnedRight && (
              <div
                className="sticky right-0 z-[2] flex items-center self-stretch bg-white pl-3 pr-7 py-2 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
                style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px` }}
              >
                {renderBodyGroup(rightCols, item, index)}
              </div>
            )}
            {onTradeEntry && (
              <div className="flex items-center justify-end px-2" style={{ width: '120px', minWidth: '120px', flexShrink: 0 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); e.preventDefault();
                    onTradeEntry(item);
                  }}
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 transition-opacity flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                  title="Create Trade Entry" type="button"
                >
                  <PencilSquareIcon className="h-3 w-3" />
                  Trade Entry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
});
